import React from "react";
import { motion } from "framer-motion";
import { Search, Facebook, Music2, Share2, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SectionHeading } from "@/components/effects/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";

const ease = [0.22, 1, 0.36, 1];

const iconMap = {
  "google-ads": Search,
  "meta-ads": Facebook,
  "tiktok-ads": Music2,
  "social-media": Share2,
};

const barHeights = [42, 64, 50, 82, 58, 94, 70, 60];

const MockPanel = ({ item, Icon }) => (
  <div className="glass relative overflow-hidden rounded-3xl p-6 shadow-card sm:p-7">
    <div className="absolute -right-12 -top-12 h-44 w-44 rounded-full bg-primary/15 blur-3xl" />

    {/* window header */}
    <div className="relative flex items-center gap-1.5">
      <span className="h-3 w-3 rounded-full bg-muted-foreground/30" />
      <span className="h-3 w-3 rounded-full bg-muted-foreground/30" />
      <span className="h-3 w-3 rounded-full bg-muted-foreground/30" />
      <div className="ml-auto flex items-center gap-2 rounded-lg border border-border/50 bg-secondary/50 px-2.5 py-1 text-xs font-medium text-muted-foreground">
        <Icon className="h-3.5 w-3.5 text-primary" />
        {item.title}
      </div>
    </div>

    {/* metrics */}
    <div className="relative mt-5 grid grid-cols-2 gap-3">
      {item.metrics.map((m) => (
        <div key={m.label} className="rounded-2xl border border-border/50 bg-secondary/30 p-4">
          <p className="min-h-[2.25rem] text-xs leading-snug text-muted-foreground">{m.label}</p>
          <p className="mt-1 font-display text-2xl font-bold text-primary">{m.value}</p>
        </div>
      ))}
    </div>

    {/* animated bar chart */}
    <div className="relative mt-4 flex h-28 items-end gap-2 rounded-2xl border border-border/50 bg-secondary/20 p-4">
      {barHeights.map((h, i) => (
        <motion.span
          key={i}
          initial={{ height: "8%" }}
          whileInView={{ height: `${h}%` }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: i * 0.06, ease }}
          className="flex-1 rounded-md bg-gradient-warm"
        />
      ))}
    </div>

    {/* feature chips */}
    <div className="relative mt-4 flex flex-wrap gap-2">
      {item.features.map((f) => (
        <span
          key={f}
          className="rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
        >
          {f}
        </span>
      ))}
    </div>
  </div>
);

export const Services = () => {
  const { t } = useLanguage();

  return (
    <section id="services" className="relative overflow-hidden py-24 sm:py-32">
      <div className="absolute right-0 top-1/4 h-96 w-96 rounded-full bg-primary/10 blur-[150px]" />
      <div className="absolute left-0 bottom-1/4 h-96 w-96 rounded-full bg-accent/10 blur-[150px]" />

      <div className="relative mx-auto max-w-7xl px-6">
        <SectionHeading tag={t.services.tag} title={t.services.title} subtitle={t.services.subtitle} />

        <div className="mt-16 space-y-10 sm:space-y-16">
          {t.services.items.map((item, i) => {
            const Icon = iconMap[item.slug] || Search;
            const reversed = i % 2 === 1;
            return (
              <div key={item.slug} id={item.slug} className="scroll-mt-28">
                <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-14">
                  {/* Text */}
                  <motion.div
                    initial={{ opacity: 0, x: reversed ? 48 : -48 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease }}
                    className={reversed ? "lg:order-2" : ""}
                  >
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-warm text-primary-foreground shadow-glow-soft">
                      <Icon className="h-6 w-6" />
                    </span>
                    <h3 className="mt-5 font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
                      {item.title}
                    </h3>
                    <p className="mt-1.5 font-display text-xl font-semibold text-gradient sm:text-2xl">
                      {item.sub}
                    </p>
                    <p className="mt-4 max-w-md text-base leading-relaxed text-muted-foreground">
                      {item.desc}
                    </p>
                    <Button variant="hero" size="lg" className="mt-7" asChild>
                      <a href="#contact">
                        {t.services.cta}
                        <ArrowUpRight className="h-4 w-4" />
                      </a>
                    </Button>
                  </motion.div>

                  {/* Visual */}
                  <motion.div
                    initial={{ opacity: 0, x: reversed ? -48 : 48, scale: 0.96 }}
                    whileInView={{ opacity: 1, x: 0, scale: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease }}
                    className={reversed ? "lg:order-1" : ""}
                  >
                    <MockPanel item={item} Icon={Icon} />
                  </motion.div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
